import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import propTypes from 'prop-types';
import * as JsSearch from 'js-search';
import { SvgIcon } from 'components/ui';

const defaultRows = 10;
const initialPage = 1;

const Table = ({
  name,
  label,
  columns,
  rows,
  children,
  setUpdatedData,
  totalCount,
  isResetColumns,
  setResetColumns,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(defaultRows);
  const [tableRange, setTableRange] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentPageRows, setCurrentPageRows] = useState([]);

  // search params
  const [search, setSearch] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * rebuilds the overall index based on the options
   */
  const rebuildIndex = () => {
    const dataToSearch = new JsSearch.Search('Title');
    // defines an indexing strategy for the data
    dataToSearch.indexStrategy = new JsSearch.PrefixIndexStrategy();
    // defines the sanitizer for the search to prevent some of the words from being excluded
    dataToSearch.sanitizer = new JsSearch.LowerCaseSanitizer();
    // defines the search index
    dataToSearch.searchIndex = new JsSearch.TfIdfSearchIndex('ID');

    dataToSearch.addIndex('Title'); // sets the index attribute for the data
    dataToSearch.addIndex('Author'); // sets the index attribute for the data
    dataToSearch.addIndex('PublicationYear'); // sets the index attribute for the data

    dataToSearch.addDocuments(rows); // adds the data to be searched
    setSearch(dataToSearch);
  };

  /**
   * handles the input change and perform a search with js-search
   * in which the results will be added to the state
   */
  const searchData = (searchText) => {
    if (Object.keys(search).length > 0) {
      setSearchQuery(searchText);
    }
  };

  // pagination code
  const LEFT_PAGE = 'LEFT';
  const RIGHT_PAGE = 'RIGHT';
  const GAP_TEXT = '...';
  const pageNeighbours = 1;

  const range = (from, to, step = 1) => {
    let i = from;
    const range = [];
    while (i <= to) {
      range.push(i);
      i += step;
    }
    return range;
  };

  const calculateRange = () => {
    const range = [];
    const queryResults = searchQuery === '' ? rows : search.search(searchQuery);
    const num = Math.ceil(queryResults.length / rowsPerPage);
    for (let i = 1; i <= num; i++) {
      range.push(i);
    }
    return range;
  };

  const sliceData = (page) => {
    const queryResults = searchQuery === '' ? rows : search.search(searchQuery);
    return queryResults.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  };

  useEffect(() => {
    rebuildIndex();
  }, [rows, searchQuery, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [rows, searchQuery]);

  useEffect(() => {
    if (Object.keys(search).length > 0) {
      const queryResult = search.search(searchQuery);
      if (setUpdatedData) {
        setUpdatedData(searchQuery !== '' ? queryResult : rows);
      }
    }
    setTableRange([...calculateRange()]);
    if (currentPage > 1) {
      const queryResults =
        searchQuery === '' ? rows : search.search(searchQuery);
      const currentPageRowIndex = queryResults.findIndex(
        (row) => row.ID === currentPageRows[0].ID
      );
      setCurrentPage(
        currentPage > 1
          ? Math.ceil((currentPageRowIndex + 1) / rowsPerPage)
          : currentPage
      );
    } else {
      setCurrentPageRows([...sliceData(1)]);
    }
  }, [search]);

  useEffect(() => {
    if (isResetColumns) {
      setSearchQuery('');
      setCurrentPage(initialPage);
      setRowsPerPage(defaultRows);
      setResetColumns(false);
    }
  }, [isResetColumns]);

  useEffect(() => {
    if (currentPage > 0) {
      setCurrentPageRows([...sliceData(currentPage)]);
    }
  }, [currentPage]);

  //  fetch page numbers based on total pages
  const fetchPageNumbers = () => {
    const totalPages = calculateRange();

    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = pageNeighbours * 3 + 1;
    const totalBlocks = totalNumbers + 1;

    if (totalPages.length > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(
        totalPages.length - 1,
        currentPage + pageNeighbours
      );
      let pages = range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages.length - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      let response = [];
      switch (true) {
        // handle: < (1) {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          pages = [GAP_TEXT, ...extraPages, ...pages];
          response = [LEFT_PAGE, 1, ...pages, totalPages.length, RIGHT_PAGE];
          break;
        }

        // handle: (1) {2 3} [4] {5 6} (10) >
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          pages = [...pages, ...extraPages, GAP_TEXT];
          response = [LEFT_PAGE, 1, ...pages, totalPages.length, RIGHT_PAGE];
          break;
        }

        // handle: < (1) {4 5} [6] {7 8} (10) >
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [GAP_TEXT, ...pages, GAP_TEXT];
          response = [LEFT_PAGE, 1, ...pages, totalPages.length, RIGHT_PAGE];
          break;
        }
      }

      return response;
    } else {
      return [LEFT_PAGE, ...range(1, totalPages.length), RIGHT_PAGE];
    }
  };

  const pages = fetchPageNumbers();

  const handleClick = (evt, page) => {
    evt.preventDefault();
    setCurrentPage(page);
  };

  const handleMoveLeft = (evt) => {
    evt.preventDefault();
    if (currentPage - pageNeighbours > 0) {
      setCurrentPage(currentPage - pageNeighbours);
    }
  };

  const handleMoveRight = (evt) => {
    evt.preventDefault();
    if (currentPage + pageNeighbours <= tableRange.length) {
      setCurrentPage(currentPage + pageNeighbours);
    }
  };

  const handleChange = (e) => {
    setRowsPerPage(Number(e.target.value));
  };

  const numberWithCommas = (count) => {
    return count
      ? count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : count;
  };

  const getTotalCount = () => {
    const totalLength =
      searchQuery !== '' ? search.search(searchQuery).length : rows.length;
    return currentPage * rowsPerPage >= totalLength
      ? totalLength
      : currentPage * rowsPerPage;
  };

  const getPaginationCounts = () => {
    const totalLength =
      searchQuery !== '' ? search.search(searchQuery).length : rows.length;
    const startingIndex = (currentPage - 1) * rowsPerPage + 1;
    const endingIndex = getTotalCount();
    return (
      <span>
        Showing {endingIndex > 0 ? startingIndex : 0} to {endingIndex} of{' '}
        {numberWithCommas(totalLength)} entries
        {endingIndex > 0 && totalCount !== totalLength && (
          <span className="filtered-text">
            {' '}
            (filtered from {numberWithCommas(totalCount)} total entries)
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="grid-container table-ui margin-bottom-3">
      <div className="grid-row">
        <div className="usa-table-container--scrollable desktop:grid-col-12">
          <div className="blue-bg padding-left-2 padding-right-2">
            <div className="grid-row">
              <div className="desktop:grid-col-9 tablet:grid-col-8">
                <div className="grid-row">
                  <div className="desktop:grid-col-2 tablet:grid-col-3 mobile:grid-col-4 padding-top-3 padding-bottom-3 search-section">
                    <label
                      htmlFor="show-entries-dropdown"
                      className="text-base-lightest table-text-font"
                    >
                      Show entries
                    </label>
                  </div>
                  <div className="desktop:grid-col-1 tablet:grid-col-2 mobile:grid-col-3 dropdown-section">
                    <select
                      id="show-entries-dropdown"
                      className="usa-select desktop:margin-top-2 tablet:margin-top-2 mobile:margin-top-2 table-per-page"
                      name={'entrycount'}
                      value={rowsPerPage}
                      onChange={(e) => handleChange(e)}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="desktop:grid-col-3 tablet:grid-col-4">
                <h3 className="mobile:margin-top-0 desktop:margin-top-2 tablet:margin-top-2">
                  <section aria-label="Small search component">
                    <div className="usa-search usa-search--small" role="search">
                      <label
                        className="usa-sr-only"
                        htmlFor="search-field-en-small"
                      >
                        Search
                      </label>
                      <input
                        className="usa-input"
                        id="search-field-en-small"
                        type="search"
                        name="search"
                        value={searchQuery}
                        onChange={(e) => searchData(e.target.value)}
                      />
                      <button
                        className="usa-button bibliography-search-btn"
                        type="submit"
                        onClick={(e) => searchData(searchQuery)}
                      >
                        {/* <img
                          src={searchImg}
                          className="usa-search__submit-icon"
                          alt="Search"
                        /> */}
                        <SvgIcon
                          name="search_bibliography"
                        />
                      </button>
                    </div>
                  </section>
                </h3>
              </div>
            </div>
          </div>
          <table className="usa-table usa-table--borderless width-full">
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th
                    width={column.width}
                    key={index}
                    scope="col"
                    role="columnheader"
                  >
                    {column.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentPageRows.map((rowData, index) => (
                <tr key={index}>
                  <td>
                    <Link to={`/research/bibliography/${rowData.TitleId}`}>
                      {rowData.Title}
                    </Link>
                  </td>
                  <td>{rowData.PublicationYear}</td>
                  <td>
                    {rowData.Author}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            {currentPageRows.length <= 0 && (
              <h5 className='no-data-msg text-center'>No data available in table</h5>
            )}
          <div className="blue-bg desktop:padding-left-2 desktop:padding-right-2">
            <div className="grid-row padding-bottom-3">
              <div className="desktop:grid-col-7 desktop:padding-right-0 tablet:grid-col-12">
                <h4 className="text-base-lightest table-text-font margin-top-3 tablet:margin-top-2 desktop:padding-left-0 tablet:padding-left-2 tablet:margin-bottom-0 mobile:padding-left-2 mobile:margin-bottom-0">
                  <span>{getPaginationCounts()}</span>
                </h4>
              </div>
              <div className="desktop:grid-col-5 desktop:margin-top-2 desktop:padding-right-0 tablet:padding-left-2 tablet:padding-top-1 tablet:padding-bottom-1 mobile:padding-left-2 mobile:padding-top-1 mobile:padding-bottom-1 tablet:grid-col-12">
                <nav
                  aria-label="Pagination"
                >
                  <ul className="usa-pagination__list pagination-list">
                    {pages.map((page, index) => {
                      if (page === LEFT_PAGE)
                        return (
                          <li key={index} className="margin-0">
                            <a
                              href="#"
                              className={`usa-pagination__link usa-pagination__previous-page ${
                                currentPage - pageNeighbours <= 0
                                  ? 'pagination-active-link'
                                  : ''
                              } ${
                                getTotalCount() > 0
                                  ? ' margin-right-0'
                                  : ' margin-right-2'
                              }`}
                              aria-label="Previous page"
                              onClick={handleMoveLeft}
                            >
                              <span className="text-uppercase text-base-lightest">
                                Previous
                              </span>
                            </a>
                          </li>
                        );

                      if (page === RIGHT_PAGE)
                        return (
                          <li key={index} className="margin-0">
                            <a
                              href="#"
                              className={`usa-pagination__link usa-pagination__next-page margin-left-0 ${
                                currentPage >= tableRange.length
                                  ? 'pagination-active-link'
                                  : ''
                              }`}
                              aria-label="Next page"
                              onClick={handleMoveRight}
                            >
                              <span className="text-uppercase text-base-lightest">
                                Next
                              </span>
                            </a>
                          </li>
                        );

                      if (page === GAP_TEXT)
                        return (
                          <li
                            key={index}
                            className="usa-pagination__overflow margin-0"
                            role="presentation"
                          >
                            <a
                              href="#"
                              className={`usa-pagination__link usa-pagination__next-page margin-left-0`}
                              aria-label="Next page"
                            >
                              <span className="text-uppercase text-base-lightest">
                                …
                              </span>
                            </a>
                          </li>
                        );

                      return (
                        <li
                          key={index}
                          className={`margin-1 page-item${
                            currentPage === page ? ' pagination-active' : ''
                          }`}
                        >
                          <a
                            href="#"
                            className={`pagination-btn text-base-lightest ${
                              currentPage === page
                                ? 'pagination-active-link'
                                : ''
                            }`}
                            onClick={(e) => handleClick(e, page)}
                            aria-label={`Page ${page}`}
                            aria-current={`${
                              currentPage === page ? 'page' : ''
                            }`}
                          >
                            {page}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p>Last Updated: August 2024</p>
    </div>
  );
};

export default Table;
Table.propTypes = {
  name: propTypes.oneOfType([propTypes.string]),
  label: propTypes.oneOfType([propTypes.string]),
};
Table.defaultProps = {
  name: '',
  label: '',
};
